import { Button } from '@/app/common/components/button/Button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/app/common/components/Card'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import { StateList } from '@/queries/common/requests'
import { ProviderQueries } from '@/queries/providers/queries'
import { StateQueries } from '@/queries/states/queries'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import * as z from 'zod'
import { useUpdatePreviewSettings } from '../hooks/useUpdatePreviewSettings'
import { ProviderPreviewSettings } from '@/schemas/provider.schema'
import AdminAuthGuard from '../../../admin-auth-guard'

const locationInfoSchema = z.object({
  location: z.object({
    streetAddress: z
      .string()
      .min(5, 'Address must be at least 5 characters')
      .max(100, 'Address must be less than 100 characters')
      .optional(),
    city: z
      .string()
      .min(2, 'City must be at least 2 characters')
      .max(50, 'City must be less than 50 characters'),
    state: z.string(),
    postalCode: z.string().refine((value) => {
      return /^\d{5}(-\d{4})?$/.test(value)
    }, 'Invalid postal code format'),
  }),
})

type LocationInfoFormType = z.infer<typeof locationInfoSchema>

type Props = {
  states: StateList
  providerId: string
  provider: ProviderPreviewSettings
}

const LocationInfoCard = (props: Props) => {
  const { states, providerId, provider } = props
  const programId = useSelector(selectSelectedProgram).id

  const providerQuery = useQuery(
    ProviderQueries.getProviderById(programId, providerId!)
  )
  const statesQuery = useQuery(StateQueries.getStateByCountry(3))

  const locationInfoMutation = useUpdatePreviewSettings({
    providerId,
  })

  const form = useForm<LocationInfoFormType>({
    resolver: zodResolver(locationInfoSchema),
    defaultValues: {
      location: {
        streetAddress: provider.address ?? undefined,
        city: provider.city ?? undefined,
        postalCode: provider.zipcode ?? undefined,
        state: provider.global_state_id?.toString() ?? undefined,
      },
    },
  })

  const onSubmit = (data: LocationInfoFormType) => {
    locationInfoMutation.mutate({
      address: data.location.streetAddress,
      city: data.location.city,
      zipcode: data.location.postalCode,
      global_state_id: data.location.state,
      program_id: programId,
    })
  }

  if (providerQuery.isLoading || statesQuery.isLoading) {
    return (
      <Card id="location-info">
        <CardHeader>
          <h3 className="text-lg font-semibold text-gray-900">
            Location Information
          </h3>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card id="location-info">
      <CardHeader>
        <h3 className="text-lg font-semibold text-gray-900">
          Location Information
        </h3>
      </CardHeader>
      <CardContent>
        <form id="location-info-form" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="streetAddress">Street Address (Optional)</Label>
              <Controller
                control={form.control}
                name="location.streetAddress"
                render={({ field }) => (
                  <Input
                    {...field}
                    className="shadow-none w-full"
                    placeholder="Enter street address"
                  />
                )}
              />
              {form.formState.errors.location?.streetAddress && (
                <p className="mt-1 text-sm text-red-600">
                  {form.formState.errors.location?.streetAddress?.message}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label htmlFor="city">City (Optional)</Label>
                <Input
                  {...form.register('location.city')}
                  id="city"
                  type="text"
                  className="shadow-none"
                  placeholder="Enter city"
                />
                {form.formState.errors.location?.city && (
                  <p className="mt-1 text-sm text-red-600">
                    {form.formState.errors.location?.city?.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="state">State/Province (Optional)</Label>
                <Controller
                  control={form.control}
                  name="location.state"
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value?.toString()}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select state or province" />
                      </SelectTrigger>
                      <SelectContent>
                        {states.data.map((state) => (
                          <SelectItem
                            key={state.id}
                            value={state.id.toString()}
                          >
                            {state.name || ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {form.formState.errors.location?.state && (
                  <p className="mt-1 text-sm text-red-600">
                    {form.formState.errors.location?.state?.message}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label htmlFor="postalCode">Postal Code (Optional)</Label>
                <Input
                  {...form.register('location.postalCode')}
                  id="postalCode"
                  type="text"
                  className="shadow-none"
                  placeholder="Enter postal code"
                />
                {form.formState.errors.location?.postalCode && (
                  <p className="mt-1 text-sm text-red-600">
                    {form.formState.errors.location?.postalCode?.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex justify-end py-4">
        <AdminAuthGuard requiredPermissions={['provider_management_edit']}>
          <Button
            type="submit"
            form="location-info-form"
            disabled={locationInfoMutation.isPending}
          >
            {locationInfoMutation.isPending
              ? 'Saving...'
              : 'Save Location Information'}
          </Button>
        </AdminAuthGuard>
      </CardFooter>
    </Card>
  )
}

export default LocationInfoCard
